import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import FetchFailed from 'reusable/FetchFailed';
import { RemoteSearch } from './useRemoteSearch';

interface RemoteListFooterProps<T> {
  search: RemoteSearch<T>;
  /** 読み終えて1件も無いときに出す文（既定は「見つかりませんでした」）。 */
  empty?: ReactNode;
  /** 失敗の知らせの id（検索の欄の aria-describedby から結ぶ）。 */
  errorId?: string;
  /**
   * false なら、1ページ目の読み込み中・空・失敗は出さない（呼び出し元が既存の表示で出すとき）。続きの
   * 「もっと見る」と、続きを読めなかったときの失敗だけを出す。
   */
  showStatus?: boolean;
}

/**
 * EV-493。候補を並べる一覧（運転者・作業・配送先）の終わりに置く、読み込み中・空・失敗・続きの表示。
 * 一覧の行は呼び出し元の見た目のまま並べ、続きは「もっと見る」のボタンで読む（キーボードで押せる）。
 * 読めなかったら既存の FetchFailed（文言・失敗の種類・再読み込み）を出す。
 */
function RemoteListFooter<T>({ search, empty, errorId, showStatus = true }: RemoteListFooterProps<T>) {
  const { t } = useTranslation('common');
  const remaining =
    search.total !== undefined ? Math.max(search.total - search.items.length, 0) : undefined;
  return (
    <>
      {(showStatus && search.status === 'loading') || search.loadingMore ? (
        <p role="status" className="px-2 py-2 text-caption text-gray-700">
          {t('ui.loading')}
        </p>
      ) : null}
      {showStatus && search.status === 'loaded' && search.items.length === 0 ? (
        <p role="status" className="px-2 py-2 text-gray-700">
          {empty ?? t('ui.no-results')}
        </p>
      ) : null}
      {search.error && (showStatus || search.status === 'loaded') ? (
        <FetchFailed id={errorId} compact error={search.error} onRetry={search.retry} />
      ) : null}
      {search.status === 'loaded' && search.hasMore && !search.loadingMore && !search.error ? (
        <button
          type="button"
          className="w-full px-2 py-2 text-primary/300 underline text-left"
          onClick={search.loadMore}
        >
          {remaining !== undefined ? t('ui.load-more-count', { count: remaining }) : t('ui.load-more')}
        </button>
      ) : null}
    </>
  );
}

export default RemoteListFooter;
